import React, { useState, useEffect } from 'react';
import { Manga, KnownSite, TIER_CONFIGS } from '../types/manga';
import { formatImageUrl, getFlagUrl, getStatusBadge, getSourceName, getTodayKey } from '../utils/helpers';
import { checkMangaSources, autoDiscoverMangaSources } from '../services/scraper';
import { X, ExternalLink, RefreshCw, BookOpen, AlertCircle, Search, Globe, Trash2, Copy, Check, Download, HardDrive } from 'lucide-react';
import { MangaReaderModal } from './MangaReaderModal';
import { OfflineDownloadModal } from './OfflineDownloadModal';
import { showAlert } from './ConfirmDialog';
import { OfflineChapterListModal } from './OfflineChapterListModal';
import { getDownloadedChapters } from '../services/mangaOfflineDB';

interface SourceDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  manga: Manga | null;
  onUpdateManga: (updated: Manga) => void;
  knownSites?: KnownSite[];
}

export const SourceDetailsModal: React.FC<SourceDetailsModalProps> = ({
  isOpen,
  onClose,
  manga,
  onUpdateManga,
  knownSites = [],
}) => {
  const [isChecking, setIsChecking] = useState(false);
  const [isDiscovering, setIsDiscovering] = useState(false);
  const [checkLogs, setCheckLogs] = useState<string[]>([]);
  const [copiedIdx, setCopiedIdx] = useState<number | null>(null);
  const [copiedTitle, setCopiedTitle] = useState(false);

  // Offline Manga states
  const [isOfflineListOpen, setIsOfflineListOpen] = useState(false);
  const [isOfflineDownloadOpen, setIsOfflineDownloadOpen] = useState(false);
  const [isReaderOpen, setIsReaderOpen] = useState(false);
  const [readerChapterNum, setReaderChapterNum] = useState<number>(1);
  const [readerAvailableChapters, setReaderAvailableChapters] = useState<number[]>([]);
  const [downloadedCount, setDownloadedCount] = useState<number>(0);

  const refreshDownloadedCount = () => {
    if (manga?.id) {
      getDownloadedChapters(manga.id)
        .then((list) => {
          setDownloadedCount(list.length);
          setReaderAvailableChapters(list.map((c) => c.chapterNum).sort((a, b) => a - b));
        })
        .catch(() => setDownloadedCount(0));
    }
  };

  useEffect(() => {
    if (isOpen && manga?.id) {
      refreshDownloadedCount();
    }
  }, [isOpen, manga?.id]);

  const handleOpenReader = (chapterNum: number, availableList: number[]) => {
    setReaderChapterNum(chapterNum);
    setReaderAvailableChapters(availableList);
    setIsReaderOpen(true);
  };

  const handleMarkRead = (chapterNum: number) => {
    if (manga && chapterNum > (manga.readCh || 0)) {
      onUpdateManga({
        ...manga,
        readCh: chapterNum,
      });
    }
  };

  const handleCopyTitle = async () => {
    if (!manga?.title) return;
    try {
      await navigator.clipboard.writeText(manga.title);
      setCopiedTitle(true);
      setTimeout(() => setCopiedTitle(false), 2000);
    } catch (e) {
      console.error('Failed to copy title:', e);
    }
  };

  const handleCopyUrl = (url: string, idx: number) => {
    navigator.clipboard.writeText(url);
    setCopiedIdx(idx);
    setTimeout(() => setCopiedIdx(null), 2000);
  };

  // Always reset logs whenever modal opens, closes, or switches to another manga!
  useEffect(() => {
    setCheckLogs([]);
    setCopiedTitle(false);
  }, [isOpen, manga?.id]);

  const handleClose = () => {
    setCheckLogs([]);
    onClose();
  };

  // Prevent background scroll when open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = '';
      };
    }
  }, [isOpen]);

  if (!isOpen || !manga) return null;

  const flagUrl = getFlagUrl(manga.nationality);
  const statusInfo = getStatusBadge(manga);
  const coverSrc = formatImageUrl(manga.imageUrl);
  const tierConfig = TIER_CONFIGS[manga.grade] || TIER_CONFIGS.C;

  // Check existing sources
  const handleCheckThisManga = async () => {
    if (!manga.sources || manga.sources.length === 0) {
      showAlert('ยังไม่มีลิงก์แหล่งข้อมูล', 'ลองกดปุ่ม "หาเว็บเพิ่ม" ด้านบน');
      return;
    }

    setIsChecking(true);
    setCheckLogs([]);

    try {
      const { updatedManga } = await checkMangaSources(manga, (msg) => {
        setCheckLogs((prev) => [...prev, msg]);
      });
      onUpdateManga(updatedManga);
    } catch (err: any) {
      setCheckLogs((prev) => [...prev, `เกิดข้อผิดพลาด: ${err.message}`]);
    } finally {
      setIsChecking(false);
    }
  };

  // Auto-discover missing sources from Known Sites
  const handleAutoDiscover = async () => {
    setIsDiscovering(true);
    setCheckLogs([]);

    try {
      const { updatedManga, discoveredCount } = await autoDiscoverMangaSources(
        manga,
        knownSites,
        (msg) => {
          setCheckLogs((prev) => [...prev, msg]);
        }
      );
      if (discoveredCount > 0) {
        onUpdateManga(updatedManga);
      }
    } catch (err: any) {
      setCheckLogs((prev) => [...prev, `เกิดข้อผิดพลาด: ${err.message}`]);
    } finally {
      setIsDiscovering(false);
    }
  };

  const handleDeleteSource = (sourceIndex: number) => {
    if (!manga) return;
    const updatedSources = (manga.sources || []).filter((_, idx) => idx !== sourceIndex);

    let maxCh = 0;
    let bestSource: any = null;
    for (const s of updatedSources) {
      if ((s.chapter || 0) > maxCh) {
        maxCh = s.chapter || 0;
        bestSource = s;
      }
    }

    const updatedManga: Manga = {
      ...manga,
      sources: updatedSources,
      currentCh: maxCh > 0 ? maxCh : (updatedSources.length === 0 ? 0 : manga.currentCh),
      latestSourceUrl: bestSource ? bestSource.url : (updatedSources[0]?.url || ''),
      latestSourceName: bestSource ? bestSource.name : (updatedSources[0]?.name || ''),
    };

    onUpdateManga(updatedManga);
    setCheckLogs((prev) => [...prev, '✓ ลบแหล่งข้อมูลที่เลือกออกเรียบร้อย']);
  };

  const tileClass =
    'flex flex-col items-center justify-center gap-1 py-2.5 px-1.5 rounded-lg bg-white/[0.03] ring-1 ring-inset ring-white/[0.06] hover:bg-white/[0.06] transition-colors text-center active:scale-[0.98]';

  return (
    <>
      <div className="modal-overlay safe-area-modal">
        <div onClick={(e) => e.stopPropagation()} className="modal-panel max-w-lg">
          {/* Fixed Header */}
          <div className="modal-header">
            <h3 className="modal-title">{manga.title}</h3>

            <div className="flex items-center gap-1 shrink-0 -mr-2">
              <button
                type="button"
                onClick={handleCopyTitle}
                title={copiedTitle ? 'คัดลอกชื่อเรื่องแล้ว' : 'คัดลอกชื่อเรื่อง'}
                className={`btn btn-sm btn-ghost px-2.5 ${copiedTitle ? 'text-emerald-400 hover:text-emerald-300' : ''}`}
              >
                {copiedTitle ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                <span className="hidden sm:inline">{copiedTitle ? 'คัดลอกแล้ว' : 'คัดลอกชื่อ'}</span>
              </button>

              <button onClick={handleClose} title="ปิด" className="icon-btn">
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Scrollable Content with native smooth scrolling */}
          <div className="modal-body space-y-6" style={{ WebkitOverflowScrolling: 'touch' }}>
            {/* Top Info */}
            <div className="flex gap-4 items-start">
              <div className="w-20 sm:w-24 aspect-[3/4] rounded-lg overflow-hidden bg-zinc-900 shrink-0 ring-1 ring-inset ring-white/[0.06]">
                <img src={coverSrc} alt={manga.title} referrerPolicy="no-referrer" className="w-full h-full object-cover" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-xs text-zinc-500">อ่านถึงตอน</div>
                <div className="flex flex-wrap items-center gap-2 mt-0.5">
                  <span className="text-xl font-semibold text-zinc-50 tabular-nums tracking-tight">
                    {manga.readCh}
                    <span className="text-zinc-600 font-normal"> / {manga.currentCh}</span>
                  </span>
                  <span className={`px-1.5 py-[3px] rounded text-[10px] font-medium leading-none ${statusInfo.badgeClass}`}>
                    {statusInfo.label}
                  </span>
                </div>

                <dl className="mt-3 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1.5 text-xs">
                  <dt className="text-zinc-500">ระดับ</dt>
                  <dd>
                    <span
                      className={`inline-flex items-center px-1.5 py-[3px] rounded ring-1 ring-inset text-[10px] font-semibold leading-none ${tierConfig.badge}`}
                    >
                      {manga.grade}
                    </span>
                    <span className="ml-1.5 text-zinc-300">{tierConfig.subtitle}</span>
                  </dd>
                  {flagUrl && (
                    <>
                      <dt className="text-zinc-500">สัญชาติ</dt>
                      <dd className="flex items-center gap-1.5 text-zinc-300">
                        <img src={flagUrl} alt="" className="w-4 h-3 rounded-[2px] object-cover" />
                      </dd>
                    </>
                  )}
                  {manga.latestSourceName && (
                    <>
                      <dt className="text-zinc-500">ล่าสุดจาก</dt>
                      <dd className="text-zinc-300 truncate">{manga.latestSourceName}</dd>
                    </>
                  )}
                </dl>
              </div>
            </div>

            {/* Offline Reader & Download Section */}
            <section>
              <div className="flex items-baseline justify-between gap-2 mb-2.5">
                <h4 className="text-sm font-semibold text-zinc-100">อ่านออฟไลน์</h4>
                <span className={`text-[11px] ${downloadedCount > 0 ? 'text-emerald-400' : 'text-zinc-500'}`}>
                  {downloadedCount > 0 ? `โหลดแล้ว ${downloadedCount} ตอน` : 'ยังไม่มีตอนที่โหลดไว้'}
                </span>
              </div>

              <div className="grid grid-cols-3 gap-2">
                <button
                  type="button"
                  onClick={() => setIsOfflineListOpen(true)}
                  className={tileClass}
                  title="เปิดดูรายการตอนที่โหลดไว้และเปิดอ่าน"
                >
                  <BookOpen className="w-4 h-4 text-zinc-300" />
                  <span className="text-[11px] sm:text-xs font-medium text-zinc-100">ตอนที่โหลดแล้ว</span>
                  <span className="text-[10px] text-zinc-500">
                    {downloadedCount > 0 ? `${downloadedCount} ตอนพร้อมอ่าน` : 'เปิดคลังอ่าน'}
                  </span>
                </button>

                <button
                  type="button"
                  onClick={() => setIsOfflineDownloadOpen(true)}
                  className={tileClass}
                  title="เลือกตอนและเว็บเพื่อดาวน์โหลด"
                >
                  <Download className="w-4 h-4 text-zinc-300" />
                  <span className="text-[11px] sm:text-xs font-medium text-zinc-100">ดาวน์โหลดตอน</span>
                  <span className="text-[10px] text-zinc-500">เลือกตอน & เว็บ</span>
                </button>

                <button
                  type="button"
                  onClick={() => setIsOfflineListOpen(true)}
                  className={tileClass}
                  title="จัดการหรือลบตอนที่ดาวน์โหลดไว้เพื่อคืนพื้นที่"
                >
                  <Trash2 className="w-4 h-4 text-rose-400" />
                  <span className="text-[11px] sm:text-xs font-medium text-zinc-100">ลบตอน</span>
                  <span className="text-[10px] text-zinc-500">คืนพื้นที่</span>
                </button>
              </div>
            </section>

            {/* Sources List */}
            <section>
              <div className="flex flex-wrap items-center justify-between gap-2 mb-2.5">
                <h4 className="text-sm font-semibold text-zinc-100">
                  แหล่งข้อมูล <span className="text-zinc-500 font-normal tabular-nums">{manga.sources?.length || 0}</span>
                </h4>
                <div className="flex items-center gap-1.5">
                  <button
                    type="button"
                    onClick={handleAutoDiscover}
                    disabled={isDiscovering || isChecking}
                    title="ค้นหาเว็บอื่นให้อัตโนมัติ"
                    className="btn btn-sm btn-secondary"
                  >
                    <Globe className={`w-3.5 h-3.5 ${isDiscovering ? 'animate-spin' : ''}`} />
                    <span>{isDiscovering ? 'กำลังค้นหา...' : 'หาเว็บเพิ่ม'}</span>
                  </button>

                  <button
                    type="button"
                    onClick={handleCheckThisManga}
                    disabled={isChecking || isDiscovering}
                    className="btn btn-sm btn-secondary"
                  >
                    <RefreshCw className={`w-3.5 h-3.5 ${isChecking ? 'animate-spin' : ''}`} />
                    <span>{isChecking ? 'กำลังเช็ค...' : 'เช็คตอน'}</span>
                  </button>
                </div>
              </div>

              {!manga.sources || manga.sources.length === 0 ? (
                <div className="py-8 rounded-xl border border-dashed border-white/[0.08] text-center text-xs text-zinc-500 flex flex-col items-center justify-center gap-3">
                  <AlertCircle className="w-5 h-5 text-zinc-500" />
                  <span>ยังไม่มีลิงก์แหล่งข้อมูล</span>
                  <button
                    type="button"
                    onClick={handleAutoDiscover}
                    disabled={isDiscovering}
                    className="btn btn-sm btn-primary"
                  >
                    <Search className="w-3.5 h-3.5" />
                    <span>ค้นหาเว็บให้อัตโนมัติ</span>
                  </button>
                </div>
              ) : (
                <div className="rounded-xl ring-1 ring-inset ring-white/[0.06] divide-y divide-white/[0.06] overflow-hidden">
                  {manga.sources.map((src, idx) => (
                    <div key={idx} className="px-3 py-2.5 flex items-center gap-3 hover:bg-white/[0.02] transition-colors">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-zinc-100 truncate">{src.name}</span>
                          <span className="px-1.5 py-[3px] rounded bg-white/[0.06] text-zinc-300 text-[10px] leading-none whitespace-nowrap tabular-nums">
                            ตอน {src.chapter || '-'}
                          </span>
                        </div>
                        <a
                          href={src.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-[11px] text-zinc-500 hover:text-zinc-300 font-mono truncate block transition-colors mt-0.5"
                          title={src.url}
                        >
                          {src.url}
                        </a>
                      </div>
                      <div className="flex items-center shrink-0 -mr-1">
                        <button
                          type="button"
                          onClick={() => handleCopyUrl(src.url, idx)}
                          title="คัดลอกลิงก์"
                          className="icon-btn w-8 h-8"
                        >
                          {copiedIdx === idx ? (
                            <Check className="w-3.5 h-3.5 text-emerald-400" />
                          ) : (
                            <Copy className="w-3.5 h-3.5" />
                          )}
                        </button>
                        <a
                          href={src.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="icon-btn w-8 h-8"
                          title="เปิดหน้าเว็บ"
                        >
                          <ExternalLink className="w-3.5 h-3.5" />
                        </a>
                        <button
                          type="button"
                          onClick={() => handleDeleteSource(idx)}
                          title="ลบแหล่งข้อมูลนี้"
                          className="icon-btn w-8 h-8 hover:text-rose-400 hover:bg-rose-500/10"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </section>

            {/* Logs */}
            {checkLogs.length > 0 && (
              <div className="p-3 rounded-xl bg-black/30 ring-1 ring-inset ring-white/[0.06] text-[11px] text-zinc-400 font-mono space-y-1 max-h-36 overflow-y-auto">
                {checkLogs.map((log, i) => (
                  <div key={i}>{log}</div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Offline Modals */}
      <OfflineChapterListModal
        isOpen={isOfflineListOpen}
        onClose={() => setIsOfflineListOpen(false)}
        manga={manga}
        onOpenReader={handleOpenReader}
        onOpenDownloader={() => setIsOfflineDownloadOpen(true)}
        onChaptersChanged={refreshDownloadedCount}
      />

      <OfflineDownloadModal
        isOpen={isOfflineDownloadOpen}
        onClose={() => setIsOfflineDownloadOpen(false)}
        manga={manga}
        onDownloadComplete={refreshDownloadedCount}
        onOpenReader={(chapterNum, list) => {
          setIsOfflineDownloadOpen(false);
          handleOpenReader(chapterNum, list);
        }}
      />

      <MangaReaderModal
        isOpen={isReaderOpen}
        onClose={() => setIsReaderOpen(false)}
        mangaId={manga.id}
        mangaTitle={manga.title}
        initialChapterNum={readerChapterNum}
        availableChapterNums={readerAvailableChapters}
        onChapterChange={(newCh) => setReaderChapterNum(newCh)}
        onMarkRead={handleMarkRead}
      />
    </>
  );
};
